import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useInstructorMode } from '../hooks/useInstructorMode';
import { formatShortDate } from '../utils/dateUtils';
import './ArtistView.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function ArtistView() {
  const { id } = useParams();
  const { buildUrl, isInstructorMode } = useInstructorMode();
  
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArtist = async () => {
      try {
        setLoading(true);
        
        // Use backend param (?show=all) when in instructor mode
        let url = `${API_BASE}/artists/${id}`;
        if (isInstructorMode) {
          url += '?show=all';
        }
        
        const response = await axios.get(url);
        setArtist(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load artist');
        console.error('Error fetching artist:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchArtist();
  }, [id, isInstructorMode]);

  if (loading) {
    return (
      <div className="artist-view loading">
        <div className="spinner"></div>
        <p>Loading artist...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="artist-view error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="artist-view error">
        <h2>Artist not found</h2>
      </div>
    );
  }

  return (
    <div className="artist-view">
      <div className="artist-header">
        <h1>{artist.name}</h1>
        <p className="artist-stats">{artist.total_songs} songs</p>
      </div>

      <div className="songs-section">
        <h2>All Songs</h2>
        <div className="songs-list">
          {artist.songs.map((song) => (
            <div key={song.id} className="song-card">
              <div className="song-main">
                <a 
                  href={buildUrl(`/song/${song.id}`)}
                  className="song-title"
                >
                  {song.name}
                </a>
                {song.collaborators && song.collaborators.length > 0 && (
                  <div className="collaborators">
                    ft. {song.collaborators.map((collab, i) => (
                      <span key={collab.id}>
                        <a href={buildUrl(`/artist/${collab.id}`)} className="collab-link">
                          {collab.name}
                        </a>
                        {i < song.collaborators.length - 1 && ', '}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="song-meta">
                {song.bpm && isInstructorMode && (
                  <span className="bpm instructor-bpm">{song.bpm} BPM</span>
                )}
                <span className="plays">{song.total_plays || 1} plays</span>
                <span className="last-played">
                  Last: {song.last_played_date ? formatShortDate(song.last_played_date) : 'Never'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="artist-actions">
        <a href={buildUrl('/')} className="btn-back">← Back to All Playlists</a>
      </div>
    </div>
  );
}

export default ArtistView;